import type {ConsolidatedReviewState} from '@github-ui/mergebox/sections/ReviewerSection'

import type {MergeabilityData} from './use-mergeability-data'
import type {MiniStatusIconProps} from './mini-merge-sections/MiniStatusIcon'
import type {ProgressSegment} from './mini-merge-sections/MultiColorProgressBar'

export type ReviewerSection = Pick<
  MergeabilityData['reviewerSection'],
  | 'numReviewsRequired'
  | 'codeownersRequired'
  | 'latestOpinionatedReviews'
  | 'pendingRequestedReviews'
  | 'sectionStatus'
  | 'consolidatedFailureReasons'
> &
  Partial<Pick<MergeabilityData['reviewerSection'], 'numReviewsApproved'>>

export type ReviewsProgressProjection = {
  blockingSummary?: string
  successful: number
  total: number
  isUnavailable: boolean
  icon: MiniStatusIconProps['type']
  status: string
  progressLabel: string
  progressValueText?: string
  segments: readonly ProgressSegment[]
}

type ReviewBlockers = {
  pendingRequiredApprovals: number
  blockingChanges: number
  hasBlockingChanges: boolean
  additionalRequirements: number
}

export function projectReviewsProgress(reviewerSection: ReviewerSection | null | undefined): ReviewsProgressProjection {
  if (!reviewerSection) {
    return {
      successful: 0,
      total: 0,
      isUnavailable: false,
      icon: 'error',
      status: 'Approvals unavailable',
      progressLabel: 'Approvals',
      progressValueText: 'Approvals unavailable',
      segments: [
        {
          color: 'neutral',
          id: 'reviews-progress-unavailable',
          label: 'Approvals unavailable',
          percentage: 100,
          testId: 'reviews-progress-unavailable',
        },
      ],
    }
  }

  const {successful, total} = getApprovalsRatio(reviewerSection)
  const blockers = getReviewBlockers(reviewerSection, successful, total)
  const segments = approvalSegments(reviewerSection, successful, total, blockers)
  const status = reviewStatusText[reviewerSection.sectionStatus]
  const required =
    reviewerSection.numReviewsRequired > 0 ||
    reviewerSection.codeownersRequired ||
    reviewerSection.sectionStatus === 'REVIEW_REQUIRED'
  const progressLabel = required
    ? `${successful} of ${total} required approvals received`
    : `${successful} of ${total} reviews approved`

  return {
    blockingSummary: reviewBlockingSummary(blockers),
    successful,
    total,
    isUnavailable: !hasReviewerContent(reviewerSection),
    icon: iconByReviewState[reviewerSection.sectionStatus],
    status,
    progressLabel,
    progressValueText: segments.length ? `${status}. ${segments.map(segment => segment.label).join(', ')}` : status,
    segments,
  }
}

function getReviewBlockers(reviewerSection: ReviewerSection, successful: number, total: number): ReviewBlockers {
  if (!hasBlockingReviewState(reviewerSection)) {
    return {pendingRequiredApprovals: 0, blockingChanges: 0, hasBlockingChanges: false, additionalRequirements: 0}
  }
  const pendingRequiredApprovals = reviewerSection.numReviewsRequired > 0 ? Math.max(total - successful, 0) : 0
  const blockingChanges = reviewerSection.latestOpinionatedReviews.filter(
    review => review.state === 'CHANGES_REQUESTED' && review.authorCanPushToRepository,
  ).length
  const hasBlockingChanges =
    reviewerSection.sectionStatus === 'CHANGES_REQUESTED' ||
    (reviewerSection.sectionStatus === 'REVIEW_REQUIRED' &&
      reviewerSection.consolidatedFailureReasons?.includes('CHANGES_REQUESTED'))
  let additionalRequirements =
    reviewerSection.consolidatedFailureReasons?.filter(
      reason =>
        reason === 'CODE_OWNER_REVIEW_REQUIRED' ||
        reason === 'REQUIRED_REVIEWER_APPROVAL_REQUIRED' ||
        reason === 'SOC2_APPROVAL_PROCESS_REQUIRED' ||
        reason === 'LAST_PUSH_APPROVAL_REQUIRED',
    ).length ?? 0
  if (
    pendingRequiredApprovals === 0 &&
    reviewerSection.consolidatedFailureReasons?.includes('MORE_REVIEWS_REQUIRED') &&
    !(
      reviewerSection.numReviewsRequired === 0 &&
      reviewerSection.consolidatedFailureReasons.includes('LAST_PUSH_APPROVAL_REQUIRED')
    )
  ) {
    additionalRequirements += 1
  }
  if (
    reviewerSection.sectionStatus === 'REVIEW_REQUIRED' &&
    pendingRequiredApprovals === 0 &&
    additionalRequirements === 0 &&
    !hasBlockingChanges
  ) {
    additionalRequirements = 1
  }
  return {
    pendingRequiredApprovals,
    blockingChanges,
    hasBlockingChanges: Boolean(hasBlockingChanges),
    additionalRequirements,
  }
}

function reviewBlockingSummary({
  pendingRequiredApprovals,
  blockingChanges,
  hasBlockingChanges,
  additionalRequirements,
}: ReviewBlockers) {
  const blockingRequirementCount =
    pendingRequiredApprovals + (hasBlockingChanges ? Math.max(blockingChanges, 1) : 0) + additionalRequirements

  if (blockingRequirementCount > 1) {
    return `${blockingRequirementCount} review requirements blocking`
  }
  if (pendingRequiredApprovals > 0) {
    return `${pendingRequiredApprovals} required ${pendingRequiredApprovals === 1 ? 'approval' : 'approvals'} still pending`
  }
  if (hasBlockingChanges) {
    return blockingChanges > 0
      ? `${blockingChanges} blocking ${blockingChanges === 1 ? 'review' : 'reviews'} requesting changes`
      : 'Changes requested are blocking'
  }
  if (additionalRequirements > 0) {
    return 'Additional review requirement blocking'
  }

  return undefined
}

function approvalSegments(
  reviewerSection: ReviewerSection,
  successful: number,
  total: number,
  {
    pendingRequiredApprovals,
    blockingChanges: changesRequested,
    hasBlockingChanges,
    additionalRequirements,
  }: ReviewBlockers,
): ProgressSegment[] {
  const blocking = hasBlockingReviewState(reviewerSection)
  const pending = reviewerSection.pendingRequestedReviews.length
  const pendingNumericApprovals =
    reviewerSection.numReviewsRequired > 0
      ? successful >= total
        ? pending
        : Math.min(pending, Math.max(total - successful, 0))
      : 0
  const pendingRequirements = blocking && reviewerSection.numReviewsRequired > 0 ? pendingNumericApprovals : pending
  const bands: Array<Omit<ProgressSegment, 'percentage'> & {weight: number}> = []
  if (reviewerSection.sectionStatus === 'APPROVED' && successful >= total) {
    bands.push({
      color: 'success',
      id: 'reviews-progress-fulfilled',
      label: 'Approval requirements fulfilled',
      testId: 'reviews-progress-fulfilled',
      weight: Math.max(total, 1),
    })
  } else {
    if (successful > 0) {
      bands.push({
        color: 'success',
        id: 'reviews-progress-approved',
        label: `${successful} approval${successful === 1 ? '' : 's'} received`,
        testId: 'reviews-progress-approved',
        weight: successful,
      })
    }
    if (hasBlockingChanges) {
      bands.push({
        color: 'danger',
        id: 'reviews-progress-changes-requested',
        label:
          changesRequested > 0
            ? `${changesRequested} blocking change request${changesRequested === 1 ? '' : 's'}`
            : 'Changes requested are blocking',
        testId: 'reviews-progress-changes-requested',
        weight: Math.max(changesRequested, 1),
      })
    }
    const outstanding = Math.max(pendingRequiredApprovals - pendingRequirements, 0)
    if (additionalRequirements > 0 || outstanding > 0) {
      bands.push({
        color: 'attention',
        id: 'reviews-progress-outstanding',
        label:
          additionalRequirements > 0
            ? 'Additional review requirement outstanding'
            : `${outstanding} approval requirement${outstanding === 1 ? '' : 's'} outstanding`,
        testId: 'reviews-progress-outstanding',
        weight: Math.max(outstanding, additionalRequirements, 1),
      })
    }
  }

  const recorded =
    reviewerSection.sectionStatus === 'REVIEWED'
      ? Math.max(reviewerSection.latestOpinionatedReviews.length - successful, 0)
      : 0
  if (recorded > 0) {
    bands.push({
      color: 'neutral',
      id: 'reviews-progress-recorded',
      label: `${recorded} review${recorded === 1 ? '' : 's'} recorded`,
      testId: 'reviews-progress-recorded',
      weight: recorded,
    })
  }

  if (pendingRequirements > 0) {
    bands.push({
      color: 'neutral',
      id: 'reviews-progress-pending',
      label: `${pendingRequirements} pending review${pendingRequirements === 1 ? '' : 's'}`,
      testId: 'reviews-progress-pending',
      weight: pendingRequirements,
    })
  }

  if (bands.length === 0 && reviewerSection.sectionStatus === 'REVIEW_REQUESTED') {
    bands.push({
      color: 'neutral',
      id: 'reviews-progress-requested',
      label: 'Review requested',
      testId: 'reviews-progress-requested',
      weight: 1,
    })
  }

  if (bands.length === 0) {
    const reviewCount = reviewerSection.latestOpinionatedReviews.length
    bands.push({
      color: 'neutral',
      id: 'reviews-progress-recorded',
      label: `${reviewCount} review${reviewCount === 1 ? '' : 's'} recorded`,
      testId: 'reviews-progress-recorded',
      weight: Math.max(reviewCount, 1),
    })
  }

  const totalWeight = bands.reduce((sum, band) => sum + band.weight, 0)
  return bands.map(({weight, ...band}) => ({...band, percentage: (weight / totalWeight) * 100}))
}

function hasReviewerContent({
  numReviewsRequired,
  codeownersRequired,
  latestOpinionatedReviews,
  pendingRequestedReviews,
  sectionStatus,
}: ReviewerSection) {
  return (
    numReviewsRequired > 0 ||
    codeownersRequired ||
    latestOpinionatedReviews.length > 0 ||
    pendingRequestedReviews.length > 0 ||
    sectionStatus !== 'REVIEWED'
  )
}

function getApprovalsRatio({
  numReviewsRequired,
  numReviewsApproved,
  codeownersRequired,
  latestOpinionatedReviews,
  pendingRequestedReviews,
  sectionStatus,
  consolidatedFailureReasons,
}: ReviewerSection) {
  const approvedReviewCount = latestOpinionatedReviews.filter(
    review => review.state === 'APPROVED' && review.authorCanPushToRepository,
  ).length
  const activeStateMinimum = sectionStatus === 'REVIEWED' ? 0 : 1

  if (numReviewsApproved !== undefined && numReviewsRequired > 0) {
    const approvalDelta = Math.max(numReviewsApproved - approvedReviewCount, 0)
    const successful = Math.max(numReviewsApproved - approvalDelta, 0)
    const total = Math.max(numReviewsRequired - approvalDelta, successful, activeStateMinimum)
    return {
      successful: sectionStatus === 'APPROVED' || numReviewsApproved >= numReviewsRequired ? total : successful,
      total,
    }
  }

  let total: number
  if (codeownersRequired) {
    total = Math.max(numReviewsRequired, approvedReviewCount, 1)
  } else if (numReviewsRequired > 0) {
    total = numReviewsRequired
  } else {
    const reviewActivityCount = latestOpinionatedReviews.length + pendingRequestedReviews.length
    total = Math.max(reviewActivityCount, activeStateMinimum)
  }

  const numericRequirementFulfilled =
    numReviewsRequired > 0 &&
    hasBlockingReviewState({sectionStatus}) &&
    consolidatedFailureReasons !== undefined &&
    !consolidatedFailureReasons.includes('MORE_REVIEWS_REQUIRED')
  return {
    successful:
      sectionStatus === 'APPROVED' || numericRequirementFulfilled ? total : Math.min(approvedReviewCount, total),
    total,
  }
}

function hasBlockingReviewState({sectionStatus}: Pick<ReviewerSection, 'sectionStatus'>) {
  return sectionStatus === 'REVIEW_REQUIRED' || sectionStatus === 'CHANGES_REQUESTED'
}

const iconByReviewState: Record<ConsolidatedReviewState, MiniStatusIconProps['type']> = {
  APPROVED: 'success',
  CHANGES_REQUESTED: 'failure',
  REVIEW_REQUIRED: 'warning',
  REVIEWED: 'neutral',
  REVIEW_REQUESTED: 'warning',
}

const reviewStatusText: Record<ConsolidatedReviewState, string> = {
  APPROVED: 'Approved',
  CHANGES_REQUESTED: 'Changes requested',
  REVIEW_REQUIRED: 'Review required',
  REVIEWED: 'Reviewed',
  REVIEW_REQUESTED: 'Review requested',
}
